// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Pages
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import { Dropdown } from "../../../FlowConfigurationInputs";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';

// Import Recoil
import { initialActions, saveAvailable, changedFields } from "../../../../recoil-store/recoil-state";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Returns     Component
 * @Author      Abdullah Onur Yeşil
 */
function Search({ action, variables, setOpenVariable, showAlert }) {
    const { t } = useTranslation();
    const [tab, setTab] = useState(action.fields.tab?.value ? action.fields.tab.value : 'all');
    const [hideValue, setHideValue] = useState(false);

    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const label = 'CONTENT.CONFIGURATION-FORM.EXCEL.SEARCH.';
    const conditionLabel = 'CONTENT.CONFIGURATION-FORM.CONDITION.';

    const options = {
        operator: {
            data: [
                {
                    value: "eq",
                    label: `= (${t(conditionLabel + 'EQ')})`,
                },
                {
                    value: "ne",
                    label: `≠ (${t(conditionLabel + 'NE')})`,
                },
                {
                    value: "empty",
                    label: `∅ (${t(conditionLabel + 'EMPTY')})`,
                },
                {
                    value: "nempty",
                    label: t(conditionLabel + 'NEMPTY'),
                },
                {
                    value: "contains",
                    label: t(conditionLabel + 'CONTAINS'),
                }
            ],
            defaultValue: "eq",
        }
    };

    useEffect(() => {
        // Assign initial values
        if (!action.fields.tab) {
            action.fields.tab = { value: 'all' };
        } else {
            setTab(action.fields.tab.value);
        }
        if (!action.fields.operator) {
            action.fields.operator = { value: 'eq', isRequired: true };
        } else {
            onChangeOperator("", { value: action.fields.operator.value })
        }
    }, [action.id]);

    // Handle tab change
    const changeTab = (e) => {
        let value = e.target.value;
        action.fields.tab = { value };
        if (value === 'all') {
            if (action.fields.columnToSearch) {
                action.fields.columnToSearch.isRequired = false;
            }
        }
        if (action.fields.set) {
            action.fields.set.value = ""
        }

        const field = initialActionsData[action.id]?.fields["tab"];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("tab", action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);

        setTab(value);
    };

    const onChangeOperator = (input, { value }) => {
        if (value === "empty" || value === "nempty") {
            setHideValue(true);
            delete action.fields.valueToSearch;
        } else {
            setHideValue(false);
        }
    }

    return (
        <>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'EXCEL-SOURCE')} is_required />
                <VariablePicker action={action} inputName="excelSource" variables={variables}
                    setOpenVariable={setOpenVariable} filter="data table" isRequired hideRequired />
                <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'data table' })}</em></small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + "OPERATOR")} />
                <Dropdown
                    action={action}
                    inputName="operator"
                    options={options.operator}
                    isRequired={true}
                    setField={onChangeOperator}
                />
            </div>
            {
                !hideValue &&
                <div className="conf-field-container">
                    <FieldLabel name={t(label + 'VALUE-TO-SEARCH')} is_required />
                    <TextInput action={action} inputName="valueToSearch" variables={variables}
                        setOpenVariable={setOpenVariable} isRequired hideRequired displayName={t(label + 'VALUE-TO-SEARCH')} />
                </div>
            }

            <div className="conf-field-container condition-tabs">
                <button className={`f-s-11 ${tab === 'all' ? 'condition-active' : ''}`}
                    value="all" onClick={changeTab}>
                    {t(label + 'SEARCH-ALL-DATA')}
                </button>
                <button className={`f-s-11 ${tab === 'column' ? 'condition-active' : ''}`}
                    value="column" onClick={changeTab}>
                    {t(label + 'SEARCH-IN-COLUMN')}
                </button>
            </div>
            {tab === 'column' &&
                <div className="conf-field-container">
                    <FieldLabel name={t(label + 'COLUMN-TO-SEARCH')} is_required />
                    <TextInput action={action} inputName="columnToSearch" variables={variables}
                        setOpenVariable={setOpenVariable} isRequired hideRequired displayName={t(label + 'COLUMN-TO-SEARCH')} fieldType="string-columns" />
                    <small className="conf-field-desc"><em>{t(label + 'COLUMN-INDEX-EG')}</em></small>
                </div>
            }
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'ASSIGN')} is_required />
                <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable}
                    filter={tab === "column" ? "array" : "data table"} isRequired hideRequired />
                <small className='conf-field-desc'><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: tab === 'column' ? 'array' : 'data table' })}</em></small>
            </div>
        </>
    );
}

export default Search;